import React, { useState, useEffect } from "react";
import {
  Routes,
  Route,
  Navigate,
  useParams,
  useNavigate,
  useLocation,
} from "react-router-dom";
import teamService from "../../services/api/teamService";
import TeamNavigation from "./TeamNavigation";
import { eid } from "../../utils/entityId";

import TeamDetails from "./TeamDetails";
import TeamManagement from "./TeamManagement";
import TeamCreator from "./TeamCreator";
import EditTeam from "./EditTeam";
import "./TeamLayout.css";

// Layout wrapper component
const TeamLayout = ({
  children,
  teamId,
  currentUser,
  isAuthenticated,
  token,
}) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMembership, setUserMembership] = useState(null);

  // Fetch user membership for the team if authenticated and teamId exists
  useEffect(() => {
    const fetchUserMembership = async () => {
      if (!isAuthenticated || !teamId || !currentUser) {
        setUserMembership(null);
        return;
      }

      try {
        // Get user's teams and find membership for this team
        const response = await teamService.getUserTeams();
        if (response.success && response.data) {
          const membership = response.data.teams?.find(
            (team) => team.id === parseInt(teamId) || team.id === teamId
          );
          setUserMembership(membership || null);
        }
      } catch (error) {
        console.error("Error fetching user membership:", error);
        setUserMembership(null);
      }
    };

    fetchUserMembership();
  }, [isAuthenticated, teamId, currentUser]);

  return (
    <div className="team-layout">
      {/* Mobile Menu Button */}
      <button
        className="mobile-menu-btn"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        aria-label="Toggle navigation menu"
      >
        <span className="hamburger-line"></span>
        <span className="hamburger-line"></span>
        <span className="hamburger-line"></span>
      </button>

      {/* Sidebar Navigation */}
      <div className={`sidebar ${sidebarOpen ? "open" : ""}`}>
        <TeamNavigation
          currentUser={currentUser}
          teamId={teamId}
          userMembership={userMembership}
        />
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div className="mobile-overlay" onClick={() => setSidebarOpen(false)} />
      )}
      {/* Main Content */}
      <main className="main-content">
        <div className="content-container">{children}</div>
      </main>
    </div>
  );
};

const TeamDetailsWrapper = ({ currentUser, isAuthenticated, token, returnPath, returnState }) => {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const returnTo = returnPath || location.state?.returnTo || "browse";

  const handleBack = () => {
    if (returnPath) {
      navigate(returnPath, returnState ? { state: returnState } : undefined);
    } else if (returnTo === "my-teams") {
      navigate("/teams/my-teams");
    } else {
      navigate(-1);
    }
  };

  return (
    <TeamLayout
      teamId={teamId}
      currentUser={currentUser}
      isAuthenticated={isAuthenticated}
      token={token}
    >
      <TeamDetails
        teamId={teamId}
        currentUser={currentUser}
        onBack={handleBack}
      />
    </TeamLayout>
  );
};

// /teams/:id/stats, /manage, /coach and /scores rendered dashboards built on
// API calls that don't exist (getTeamMatches, recordPlayerScore, ...), so they
// always errored (TM-B23). Their features live on the team page (members,
// statistics) and the match pages; old links land on the team page.
const TeamSubpageRedirect = () => {
  const { teamId } = useParams();
  return <Navigate to={`/teams/${teamId}`} replace />;
};

const TeamCreatorWrapper = ({ currentUser, token, onLogout }) => {
  const navigate = useNavigate();

  const handleCancel = () => {
    navigate("/teams");
  };

  const handleTeamCreated = (team) => {
    navigate(`/teams/${eid(team)}`);
  };

  return (
    <TeamCreator
      currentUser={currentUser}
      token={token}
      onLogout={onLogout}
      onCancel={handleCancel}
      onTeamCreated={handleTeamCreated}
    />
  );
};

// Gaming UI Team Details Wrapper (no sidebar)
const TeamDetailsGamingWrapper = ({ currentUser, isAuthenticated, token, onLogout }) => {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const returnPath = location.state?.returnPath;
  const returnState = location.state?.returnState;
  const returnTo = returnPath || location.state?.returnTo || 'browse';

  const handleBack = () => {
    if (returnPath) {
      navigate(returnPath, returnState ? { state: returnState } : undefined);
    } else if (returnTo === 'my-teams') {
      navigate("/teams/my-teams");
    } else {
      navigate(-1);
    }
  };

  return (
    <TeamDetails
      teamId={teamId}
      currentUser={currentUser}
      token={token}
      onLogout={onLogout}
      onBack={handleBack}
      returnTo={returnTo}
      onJoinRequest={() => {
        // Refresh on join request
      }}
    />
  );
};

// Gaming UI Edit Team Wrapper (no sidebar)
const EditTeamWrapper = ({ currentUser, isAuthenticated, token, onLogout }) => {
  const { teamId } = useParams();
  const navigate = useNavigate();

  const handleBack = () => {
    navigate(`/teams/${teamId}`);
  };

  return (
    <EditTeam 
      currentUser={currentUser} 
      token={token}
      onLogout={onLogout}
      onBack={handleBack} 
    />
  );
};

// Main Teams App Component
const TeamsApp = ({ currentUser, isAuthenticated, token, onLogout }) => {
  // No loading state needed since main app handles authentication

  return (
    <div className="teams-app">
      <Routes>
        {/* Team Management - Main Route */}
        <Route
          path="/"
          element={
            <TeamManagement
              currentUser={currentUser}
              token={token}
              onLogout={onLogout}
            />
          }
        />

        {/* Browse Teams Route - MUST come before :teamId */}
        <Route
          path="/browse"
          element={
            <TeamManagement
              currentUser={currentUser}
              token={token}
              onLogout={onLogout}
            />
          }
        />

        {/* My Teams - Protected Route */}
        <Route
          path="/my-teams"
          element={
            isAuthenticated ? (
              <TeamManagement
                currentUser={currentUser}
                token={token}
                onLogout={onLogout}
              />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        {/* Team Creator - Protected Route */}
        <Route
          path="/create"
          element={
            isAuthenticated ? (
              <TeamCreatorWrapper
                currentUser={currentUser}
                token={token}
                onLogout={onLogout}
              />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        {/* Edit Team - Protected Route - Gaming UI */}
        <Route
          path="/:teamId/edit"
          element={
            isAuthenticated ? (
              <EditTeamWrapper
                currentUser={currentUser}
                isAuthenticated={isAuthenticated}
                token={token}
                onLogout={onLogout}
              />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        {/* Team Details - Public/Member Route - Gaming UI */}
        <Route
          path="/:teamId"
          element={
            <TeamDetailsGamingWrapper
              currentUser={currentUser}
              isAuthenticated={isAuthenticated}
              token={token}
              onLogout={onLogout}
            />
          }
        />

        {/* Team Statistics - Member Route */}
        <Route
          path="/:teamId/stats"
          element={
            <TeamSubpageRedirect />
          }
        />

        {/* Team Management - Coach/Manager Route */}
        <Route
          path="/:teamId/manage"
          element={
            <TeamSubpageRedirect />
          }
        />

        {/* Coach Dashboard - Coach/Manager Route */}
        <Route
          path="/:teamId/coach"
          element={
            <TeamSubpageRedirect />
          }
        />

        {/* Score Management - Coach/Manager Route */}
        <Route
          path="/:teamId/scores"
          element={
            <TeamSubpageRedirect />
          }
        />
      </Routes>
    </div>
  );
};

export default TeamsApp;
